/**
*
* ErrorSummary
*
*/

import React from 'react';
import PropTypes from 'prop-types';
import { PREFIX_FIELDS, PREFIX_SCREEN, SUFFIX_ERROR, DISCLAIMER_LOCATION_TYPES } from 'utils/constants';
import { generateHTMLIdFromKeyPath } from 'utils/idUtils';
import { removePseudoTags } from 'utils/coercionUtils';
import { TOP_LEVEL_ERROR_DATA } from 'langs/en/errors';
import reduce from 'lodash/reduce';

const collectErrors = (keyPath, errorTree, accumulator = []) => reduce(errorTree, (acc, curr, key) => {
  if (errorTree.error) {
    if (errorTree.error.visible) {
      acc.push({ keyPath, errorText: errorTree.error.text });
    }
    return acc;
  }
  return collectErrors([...keyPath, key], curr, acc);
}, accumulator);

class ErrorSummary extends React.Component {
  makeListElement = (errorId, errorSourceId, errorText, innerElements) => (
    <li key={errorId} id={errorId}>
      <a className="error" href={`#${errorSourceId}`}>{errorText}</a>
      {innerElements ? <ul className="dash">{innerElements}</ul> : null}
    </li>
  );

  makeTopLevelErrorLinks = (errorKey, screenOrder) => {
    if ('error' in this.props.errors[errorKey] && this.props.errors[errorKey].error.visible) {
      const errorId = `${errorKey}${SUFFIX_ERROR}`;
      const topLevelId = `tx-form-${errorKey}`;
      const innerListElement = this.makeListElement(`${errorId}-inner`, topLevelId, TOP_LEVEL_ERROR_DATA[errorKey].text);
      return this.makeListElement(errorId, topLevelId, TOP_LEVEL_ERROR_DATA[errorKey].title, innerListElement);
    }

    if (errorKey === 'syntheticContentEntities') {
      const snythethicErrorHandlers = {
        default: () => { },
        disclaimers: () => {
          const disclaimerErrors = collectErrors(['disclaimers'], this.props.errors[errorKey].disclaimers)
            .map(({ keyPath, errorText }) => {
              const disclaimerLocationIdx = keyPath.findIndex((key) => key === 'disclaimers') + 1;
              return { type: keyPath[disclaimerLocationIdx], errorText };
            });
          if (disclaimerErrors.length) {
            const els = disclaimerErrors.map((errorObj) => {
              const errorId = `disclaimer-${errorObj.type}-value${SUFFIX_ERROR}`;
              const sourceId = `${generateHTMLIdFromKeyPath(['syntheticContentEntities', 'disclaimers', errorObj.type, 'value'], 'tx-attr')}`;
              const innerError = this.makeListElement(errorId, sourceId, errorObj.errorText);
              return this.makeListElement(`disclaimer-${errorObj.type}${SUFFIX_ERROR}`, sourceId, `${errorObj.type === DISCLAIMER_LOCATION_TYPES.body ? 'Above' : 'Below'} Submit`, innerError);
            });
            return this.makeListElement('', '', 'Disclaimers', els);
          }
          return null;
        },
        gdprSetting: () => {
          if ('error' in this.props.errors[errorKey].gdprSetting && this.props.errors[errorKey].gdprSetting.error.visible) {
            const errorId = `gdprSetting${SUFFIX_ERROR}`;
            const topLevelId = `tx-form-${errorKey}-gdprSetting`;
            const innerListElement = this.makeListElement(`${errorId}-inner`, topLevelId, TOP_LEVEL_ERROR_DATA.gdprSetting.text);
            return this.makeListElement(errorId, topLevelId, TOP_LEVEL_ERROR_DATA.gdprSetting.title, innerListElement);
          }
          return null;
        },
      };
      return Object.keys(this.props.errors[errorKey])
        .map((subKey) => snythethicErrorHandlers[subKey] ? snythethicErrorHandlers[subKey]() : snythethicErrorHandlers.default())
        .filter((x) => x);
    }
    if (errorKey === 'screens') {
      const screenErrors = [];
      Object.keys(this.props.errors.screens).forEach((screenKey) => {
        if (this.props.errors.screens[screenKey].error.visible) {
          const screenErrorId = `${screenKey}${SUFFIX_ERROR}`;
          const screenErrorTitle = `Step ${screenOrder.indexOf(screenKey) + 1}`;
          const innerListElement = this.makeListElement(`${screenErrorId}-inner`, screenKey, 'Step cannot be empty.');
          screenErrors.push(this.makeListElement(screenErrorId, screenKey, screenErrorTitle, innerListElement));
        }
      });
      return screenErrors;
    }
    if (errorKey === 'fields') {
      const fieldErrors = [];
      Object.keys(this.props.errors.fields).forEach((fieldId) => {
        const fieldErrorId = `${PREFIX_FIELDS}${fieldId}${SUFFIX_ERROR}`;
        const fieldElementId = `${generateHTMLIdFromKeyPath(['fields', fieldId], 'tx')}`;
        const fieldErrorTitle = removePseudoTags(this.props.form.fields[fieldId].label) || 'New Field';
        const errorsForField = this.makeErrorListForField(['fields', fieldId], this.props.errors.fields[fieldId], fieldElementId);
        if (errorsForField.length) {
          fieldErrors.push(this.makeListElement(fieldErrorId, fieldElementId, fieldErrorTitle, errorsForField));
        }
      });
      return fieldErrors;
    }
    return [];
  }

  makeErrorListForField = (keyPath, errorTree, fieldElementId) => {
    let fieldErrors = [];
    if ('error' in errorTree && errorTree.error.visible) {
      const errorSourceId = generateHTMLIdFromKeyPath(keyPath); // Note: when links work, use this in makeListElement below
      const errorId = `${errorSourceId}${SUFFIX_ERROR}`;
      fieldErrors.push(this.makeListElement(errorId, fieldElementId, errorTree.error.text));
    }
    Object.keys(errorTree).forEach((nextKey) => {
      if (nextKey === 'error') {
        return;
      }
      const nextKeyPath = keyPath.slice().concat(nextKey);
      const recursedErrorsReturned = this.makeErrorListForField(nextKeyPath, errorTree[nextKey], fieldElementId);
      fieldErrors = fieldErrors.concat(recursedErrorsReturned);
    });
    return fieldErrors;
  }

  errorComparator = (errorElementA, errorElementB) => {
    const errorOrder = ['name', 'degreeOffering', ...this.props.form.fieldOrder];
    let keyA = errorElementA.key.slice(0, -SUFFIX_ERROR.length);
    if (keyA.indexOf(PREFIX_FIELDS) !== -1) {
      keyA = keyA.slice(7);
    }
    let keyB = errorElementB.key.slice(0, -SUFFIX_ERROR.length);
    if (keyB.indexOf(PREFIX_FIELDS) !== -1) {
      keyB = keyB.slice(7);
    }
    return errorOrder.indexOf(keyA) - errorOrder.indexOf(keyB);
  }

  render() {
    let errorElements = [];
    Object.keys(this.props.errors).forEach((id) => {
      const screenOrder = this.props.form.fieldOrder.filter((idString) => idString.indexOf(PREFIX_SCREEN) === 0);
      errorElements = errorElements.concat(this.makeTopLevelErrorLinks(id, screenOrder));
    });
    errorElements.sort(this.errorComparator);
    return (errorElements.length ? (
      <div className="error-summary">
        <div className="p-4">
          <h6 className="font-weight-bold">Please correct the following errors before saving the form.</h6>
          {errorElements.length ? <ul className="mt-4 mb-0">{errorElements}</ul> : null}
        </div>
      </div>
    ) : null);
  }
}

ErrorSummary.propTypes = {
  form: PropTypes.shape({
    fields: PropTypes.object.isRequired,
    fieldOrder: PropTypes.array.isRequired,
  }).isRequired,
  errors: PropTypes.shape({
    fields: PropTypes.object,
    screens: PropTypes.object,
  }),
};

ErrorSummary.defaultProps = {
  errors: {},
};

export default ErrorSummary;
